import { COACHING_LIBRARY } from "./coachingNotesData.js";

export function normalizeCoachingIntensity(raw) {
  const s = String(raw || "").trim().toLowerCase();
  if (s === "easy") return "Easy";
  if (s === "moderate") return "Moderate";
  if (s === "challenging") return "Challenging";
  if (s === "intense") return "Intense";
  return "Moderate";
}

/** Match planUtils.js taper rules */
export function getTaperLayout(planLength, raceDistance) {
  const total = planLength;
  if (total < 1) return { taper: 0, raceIdx: -1, taperStart: 0 };
  let taper = 2;
  if (["5K", "10K"].includes(raceDistance)) taper = 1;
  const raceIdx = total - 1;
  const taperStart = Math.max(1, raceIdx - taper);
  return { taper, raceIdx, taperStart };
}

/**
 * Tags present in this week's schedule strings (lowercase scan).
 */
export function collectWorkoutTagsFromSchedule(schedule) {
  const tags = new Set();
  const blob = Object.values(schedule || {})
    .map((v) => (typeof v === "string" ? v : ""))
    .join(" ")
    .toLowerCase();
  if (!blob.trim()) return tags;

  if (/easy run/i.test(blob)) tags.add("EASY_RUN");
  if (/\bwalk\b/i.test(blob)) tags.add("WALK");
  if (/cross training/i.test(blob)) tags.add("CROSS_TRAINING");
  if (/long run|saturday run/i.test(blob)) tags.add("LONG_OR_SAT");
  if (/tempo run/i.test(blob)) tags.add("TEMPO");
  if (/interval/i.test(blob)) tags.add("INTERVAL");
  if (/shake-?out/i.test(blob)) tags.add("SHAKEOUT");

  return tags;
}

/** e.g. "Cross Training (30 min)" → "30" */
function crossTrainingDurationFromSchedule(schedule) {
  for (const v of Object.values(schedule || {})) {
    const m = /cross training\s*\((\d+)\s*min\)/i.exec(String(v ?? ""));
    if (m) return m[1];
  }
  return null;
}

/**
 * Normalize to the week-type segment shown in the UI (matches `focus` / `label` on each week).
 * Strips a leading "Week N: " title prefix if present.
 */
function weekTypeFromLabel(raw) {
  const s = String(raw || "").trim();
  if (!s) return "";
  const m = /^week\s+\d+\s*:\s*(.+)$/i.exec(s);
  return (m ? m[1] : s).trim();
}

/**
 * Coaching library key from the same week label the plan displays — not from a parallel formula.
 * @returns {'ONBOARDING'|'BUILD'|'CUTBACK'|'PEAK'|'TAPER'|'TAPER_2'|'RACE_WEEK'}
 */
export function resolveCoachingWeekKind(
  weekIndex,
  labeledPlan,
  raceDistance
) {
  const typePart = weekTypeFromLabel(
    labeledPlan[weekIndex]?.focus ?? labeledPlan[weekIndex]?.label ?? ""
  );
  const t = typePart.toLowerCase();

  if (!typePart) return "BUILD";

  if (/race\s*week/i.test(t) || /^race$/i.test(typePart.trim())) {
    return "RACE_WEEK";
  }
  if (t.includes("onboarding")) return "ONBOARDING";
  if (/\bpeak\b/i.test(t)) return "PEAK";
  if (t.includes("cutback")) return "CUTBACK";

  if (t.includes("taper")) {
    const compact = t.replace(/\s+/g, " ");
    if (/\btaper\s*2\b|\btaper-?\s*2\b/.test(compact)) return "TAPER_2";
    const { taper, taperStart, raceIdx } = getTaperLayout(
      labeledPlan.length,
      raceDistance
    );
    if (
      taper === 2 &&
      weekIndex === taperStart + 1 &&
      weekIndex < raceIdx
    ) {
      return "TAPER_2";
    }
    return "TAPER";
  }

  if (/^base(\s+\d+)?$/i.test(typePart) || /^build(\s+\d+)?$/i.test(typePart)) {
    return "BUILD";
  }

  return "BUILD";
}

function pickPack(kind, intensity) {
  const lib = COACHING_LIBRARY[kind];
  if (!lib) return null;
  let pack = lib[intensity];
  if (pack) return pack;

  if (kind === "TAPER_2") {
    pack = COACHING_LIBRARY.TAPER?.[intensity];
    if (pack) return pack;
  }

  return lib.Moderate || lib.Easy || null;
}

/**
 * @returns {null | { title: string, bodyParagraphs: string[], guidance: {text:string}[], recovery: string|null, raceDay: string|null }}
 */
export function getCoachingForWeek({
  weekIndex,
  labeledPlan,
  raceDistance,
  intensity,
}) {
  const intKey = normalizeCoachingIntensity(intensity);
  const kind = resolveCoachingWeekKind(
    weekIndex,
    labeledPlan,
    raceDistance
  );
  const pack = pickPack(kind, intKey);
  if (!pack) return null;

  const schedule = labeledPlan[weekIndex]?.schedule;
  const tags = collectWorkoutTagsFromSchedule(schedule);
  const xTrainDuration = crossTrainingDurationFromSchedule(schedule);

  const guidance = (pack.guidance || [])
    .filter((row) => tags.has(row.match))
    .map((row) => {
      if (row.match !== "CROSS_TRAINING") return row;
      const duration = xTrainDuration ?? "20";
      return {
        ...row,
        text: String(row.text).replace(/\{duration\}/g, duration),
      };
    });

  const rawBody = Array.isArray(pack.body) ? pack.body : [pack.body];
  const bodyParagraphs = rawBody
    .filter((p) => p != null && String(p).trim() !== "")
    .map(String);

  return {
    title: pack.title,
    bodyParagraphs,
    guidance,
    recovery: pack.recovery ?? null,
    raceDay: pack.raceDay ?? null,
  };
}
